import { useEffect, useState } from "react";
import { loadMissingPrimaryGroups } from "../../services/primaryContactAudit";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

// A read-only audit modal: active DFV Services with no Primary contact nominated,
// split into "has contacts but no Primary" (fixable via Change Primary) and
// "no contacts at all" (needs a contact added first). Each row opens that service.
function Group({ title, hint, rows, onOpenShelter }) {
  return (
    <div className="mb-4">
      <div className="text-[13px] font-bold text-zephyr-text mb-[2px]">{title}</div>
      <div className="text-[11px] text-zephyr-muted mb-2">{hint}</div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-zephyr-muted italic">None.</div>
      ) : (
        rows.map(function (s) {
          return (
            <button
              key={s.id}
              type="button"
              onClick={function () {
                onOpenShelter(s.shelter_code || "");
              }}
              className="w-full text-left flex items-center justify-between gap-3 py-2 px-3 rounded-[8px] border border-zephyr-border bg-white hover:bg-[#EEF3F2] cursor-pointer mb-[6px]"
              style={{ fontFamily: "inherit" }}
            >
              <span className="text-[13px] text-zephyr-text font-semibold">
                {s.shelter_name || "(no name)"}
              </span>
              <span className="text-[12px] text-zephyr-muted">
                {[s.shelter_code, s.state].filter(Boolean).join(" · ")}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
}

export function PrimaryContactCheckModal({ open, onClose, onOpenShelter }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({ hasContactsNoPrimary: [], noContactsAtAll: [] });
  const [err, setErr] = useState("");

  useEffect(
    function () {
      if (!open) return undefined;
      let alive = true;
      setLoading(true);
      setErr("");
      loadMissingPrimaryGroups()
        .then(function (g) {
          if (alive) {
            setGroups(g);
            setLoading(false);
          }
        })
        .catch(function () {
          if (alive) {
            setErr("Could not load the primary-contact check. Please try again.");
            setLoading(false);
          }
        });
      return function () {
        alive = false;
      };
    },
    [open]
  );

  if (!open) return null;

  const total = groups.hasContactsNoPrimary.length + groups.noContactsAtAll.length;

  return (
    <ModalBackdrop
      onClose={onClose}
      draggable
      dragTitle="Check Primary Contacts"
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        width: "calc(100vw - 40px)",
        maxWidth: 560,
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <div style={{ padding: 24 }}>
        <div className="font-bold text-[16px] text-zephyr-teal-dark mb-1">
          Check Primary Contacts
        </div>
        <div className="text-[13px] text-zephyr-muted mb-4">
          Active DFV Services with no Primary contact nominated. Click a service to open it and set
          one.
        </div>

        {loading ? (
          <div className="text-center text-zephyr-muted p-6">Loading…</div>
        ) : err ? (
          <div className="bg-[#FFEBEE] text-[#C62828] rounded-[8px] py-[10px] px-[14px] text-[13px]">
            {err}
          </div>
        ) : total === 0 ? (
          <div className="text-center text-[#2E7D32] p-6 text-[14px] font-semibold">
            All active DFV Services have a Primary contact. 🎉
          </div>
        ) : (
          <>
            <Group
              title={`Has contacts but no Primary nominated (${groups.hasContactsNoPrimary.length})`}
              hint="Open the service and use Change Primary to nominate one."
              rows={groups.hasContactsNoPrimary}
              onOpenShelter={onOpenShelter}
            />
            <Group
              title={`No contacts at all (${groups.noContactsAtAll.length})`}
              hint="Add a contact or user before a Primary can be set."
              rows={groups.noContactsAtAll}
              onOpenShelter={onOpenShelter}
            />
          </>
        )}

        <div className="flex justify-end mt-4">
          <Btn variant="secondary" onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}
